<template>
  <div>
    <div class="mb-4 flex items-center gap-4">
      <Select
        v-if="props.bookingId"
        v-model="selectedCounterpartyType"
        :options="counterpartyTypeOptions"
        :placeholder="t('counterpartyType.label')"
        variant="ghost"
        empty-value-to="undefined"
      />
      <CVButton
        class="ml-auto"
        variant="secondary"
        size="sm"
        :is-loading="isRecalculating"
        data-testid="recalculate-ledger-entries"
        @click="handleRecalculate"
      >
        <RefreshCwIcon class="size-4" />
        {{ t('recalculate') }}
      </CVButton>
    </div>
    <div class="space-y-6">
      <div
        class="grid-cols-[minmax(0,1fr)_max-content_max-content_max-content_max-content_max-content_max-content_max-content]"
        :class="table()"
      >
        <div :class="tableHeader()">
          <div class="col-span-2 col-start-1">
            {{ t('description') }}
          </div>
          <div>{{ t('dueDate') }}</div>
          <div :class="tableCell({ numeric: true })">{{ t('amountToPay') }}</div>
          <div :class="tableCell({ numeric: true })">{{ t('amountToInvoice') }}</div>
          <div :class="tableCell({ numeric: true })">{{ t('amountNet') }}</div>
          <div :class="tableCell({ numeric: true })">{{ t('taxRate') }}</div>
          <div :class="tableCell({ numeric: true })">{{ t('amount') }}</div>
        </div>
        <template v-if="ledgerEntries.length">
          <template v-for="entry in ledgerEntries" :key="entry.id">
            <div
              :class="
                tableRow({
                  interactive: hasAllocations(entry.id),
                })
              "
              :role="hasAllocations(entry.id) ? 'button' : undefined"
              :tabindex="hasAllocations(entry.id) ? 0 : -1"
              :aria-expanded="
                hasAllocations(entry.id) ? !!expandedLedgerEntries[entry.id] : undefined
              "
              data-testid="ledger-entry-item"
              @click="toggleExpanded(entry.id)"
              @keydown.enter.prevent="toggleExpanded(entry.id)"
              @keydown.space.prevent="toggleExpanded(entry.id)"
            >
              <div class="flex min-w-0 items-center gap-2">
                <div class="inline-flex size-5 items-center justify-center rounded-sm">
                  <ChevronRightIcon
                    class="size-4 text-obsidian-600 transition-transform"
                    :class="{
                      'rotate-90': expandedLedgerEntries[entry.id],
                      'opacity-30': !hasAllocations(entry.id),
                    }"
                  />
                </div>
                <LedgerEntryListItemTitle :entry="entry" />
              </div>
              <div
                class="inline-block max-w-min rounded-sm px-1 py-0.5 text-xs"
                :class="getCounterpartyTypeBadgeClass(entry.counterpartyType)"
              >
                {{ t(`counterpartyTypes.${entry.counterpartyType}`) }}
              </div>
              <div>
                {{ entry.dueDate ? formatDate(entry.dueDate, dateFormat) : '-' }}
              </div>
              <div
                :class="tableCell({ numeric: true, class: getAmountTextColor(entry.amountToPay) })"
              >
                <span v-currency="entry.amountToPay / 100" />
              </div>
              <div
                :class="
                  tableCell({ numeric: true, class: getAmountTextColor(entry.amountToInvoice) })
                "
              >
                <span v-currency="entry.amountToInvoice / 100" />
              </div>
              <div
                :class="
                  tableCell({
                    numeric: true,
                    class: [getAmountTextColor(entry.amountNet), 'font-medium'],
                  })
                "
              >
                <span v-currency="entry.amountNet / 100" />
              </div>
              <div :class="tableCell({ numeric: true })">
                {{ formatPercent(entry.taxPercent / 100, { maximumFractionDigits: 2 }) }}
              </div>
              <div
                :class="
                  tableCell({
                    numeric: true,
                    class: [getAmountTextColor(entry.amount), 'font-bold'],
                  })
                "
              >
                {{ entry.amount > 0 ? '+' : '' }}{{ formatCurrency(entry.amount / 100) }}
              </div>
            </div>
            <div
              v-if="expandedLedgerEntries[entry.id]"
              class="col-span-full overflow-x-hidden border-b border-black/5 bg-obsidian-50 px-4 py-3"
            >
              <LedgerEntryAllocationDetails :entry="entry" />
            </div>
          </template>
          <div :class="tableFooter()" data-testid="ledger-entries-total">
            <div class="col-span-3 flex items-center">{{ t('total') }}</div>
            <div :class="tableCell({ numeric: true, class: 'font-semibold' })">
              <span v-currency="sumBy(ledgerEntries, 'amountToPay') / 100" />
            </div>
            <div :class="tableCell({ numeric: true, class: 'font-semibold' })">
              <span v-currency="sumBy(ledgerEntries, 'amountToInvoice') / 100" />
            </div>
            <div :class="tableCell({ numeric: true, class: 'font-semibold' })">
              <span v-currency="sumBy(ledgerEntries, 'amountNet') / 100" />
            </div>
            <div :class="tableCell({ numeric: true, class: 'font-semibold' })">-</div>
            <div :class="tableCell({ numeric: true, class: 'font-bold' })">
              {{ formatCurrency(sumBy(ledgerEntries, 'amount') / 100) }}
            </div>
          </div>
        </template>
        <EntityListExceptions
          :status="status"
          :error="error"
          :items-count="ledgerEntries.length"
          :no-items-text="t('noLedgerEntries')"
          class="col-span-full"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { CounterpartyType } from '@/entities/ledger/ledger-entry.entity.ts';
import { formatCurrency, formatPercent } from '@/utils/format-numbers.ts';
import { sumBy } from 'lodash';
import { useLedgerEntries, useRecalculateLedgerEntries } from '@/queries/use-ledger.ts';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data.ts';
import { FindAllLedgerEntriesSort, Order } from '@carvia/ros-client-types';
import EntityListExceptions from '../EntityListExceptions.vue';
import { formatDate } from '@/hooks/use-formatted-date.ts';
import { useDateFormat } from '@/hooks/use-date-format.ts';
import type { SelectOption } from '@/components/Select.vue';
import Select from '@/components/Select.vue';
import { useCounterpartyTypeOptions } from '@/hooks/use-counterparty-type-options';
import LedgerEntryListItemTitle from './LedgerEntryListItemTitle.vue';
import LedgerEntryAllocationDetails from './LedgerEntryAllocationDetails.vue';
import {
  table,
  tableCell,
  tableFooter,
  tableHeader,
  tableRow,
} from '../../../../theme/lib/cva/grid.ts';
import { getAmountTextColor } from './utils/get-amount-text-color.ts';
import { ChevronRightIcon, RefreshCwIcon } from 'lucide-vue-next';
import CVButton from '@/components/buttons/CVButton.vue';

const props = defineProps<
  | {
      bookingId: string;
      invoiceId?: never;
      paymentId?: never;
    }
  | {
      paymentId: string;
      bookingId?: never;
      invoiceId?: never;
    }
  | {
      invoiceId: string;
      bookingId?: never;
      paymentId?: never;
    }
>();

const dateFormat = useDateFormat();
const { t } = useI18n();

const selectedCounterpartyType = ref<CounterpartyType>();
const counterpartyTypeSelectOptions = useCounterpartyTypeOptions();
const counterpartyTypeOptions = computed<SelectOption[]>(() => [
  { label: t('all'), value: '' },
  ...counterpartyTypeSelectOptions.value,
]);

const { mutateAsync: recalculateLedgerEntries, isPending: isRecalculating } =
  useRecalculateLedgerEntries();

const handleRecalculate = async () => {
  await recalculateLedgerEntries(
    props.bookingId
      ? { bookingId: props.bookingId }
      : props.invoiceId
        ? { invoiceId: props.invoiceId }
        : { paymentId: props.paymentId },
  );
};

const {
  data: ledgerEntriesData,
  status,
  error,
} = useLedgerEntries(
  computed(() => ({
    bookingIds: props.bookingId ? [props.bookingId] : undefined,
    paymentIds: props.paymentId ? [props.paymentId] : undefined,
    invoiceIds: props.invoiceId ? [props.invoiceId] : undefined,
    counterpartyTypes: selectedCounterpartyType.value
      ? [selectedCounterpartyType.value]
      : undefined,
    sort: [FindAllLedgerEntriesSort.DUE_DATE],
    order: Order.ASC,
    limit: 999,
  })),
);

const ledgerEntries = useFlattenPaginatedData(ledgerEntriesData);
const expandedLedgerEntries = ref<Record<string, boolean>>({});

const hasAllocations = (entryId: string) => {
  const entry = ledgerEntries.value.find((ledgerEntry) => ledgerEntry.id === entryId);
  return !!(
    entry?.invoiceAllocations?.length ||
    entry?.paymentAllocations?.length ||
    entry?.nettingAllocations?.length
  );
};

const toggleExpanded = (entryId: string) => {
  if (!hasAllocations(entryId)) return;
  expandedLedgerEntries.value = {
    ...expandedLedgerEntries.value,
    [entryId]: !expandedLedgerEntries.value[entryId],
  };
};

const getCounterpartyTypeBadgeClass = (counterpartyType: CounterpartyType) => {
  switch (counterpartyType) {
    case CounterpartyType.CUSTOMER:
      return 'bg-blue-50 text-blue-700';
    case CounterpartyType.AGENCY:
      return 'bg-yellow-100 text-yellow-800';
    case CounterpartyType.OTHER:
      return 'bg-obsidian-50 text-obsidian-700';
  }
};
</script>

<i18n lang="json">
{
  "en": {
    "ledgerEntries": "Ledger Entries",
    "noLedgerEntries": "No ledger entries found",
    "amount": "Amount",
    "amountNet": "Amount (Net)",
    "amountToPay": "Amount to Pay",
    "amountToInvoice": "Amount to Invoice",
    "taxRate": "Tax Rate",
    "description": "Description",
    "total": "Total",
    "dueDate": "Due Date",
    "all": "All",
    "recalculate": "Recalculate"
  },
  "de": {
    "ledgerEntries": "Forderungseinträge",
    "noLedgerEntries": "Keine Forderungseinträge gefunden",
    "amount": "Betrag",
    "amountNet": "Betrag (Netto)",
    "amountToPay": "Zu zahlen",
    "amountToInvoice": "Zu fakturieren",
    "taxRate": "Steuersatz",
    "description": "Beschreibung",
    "total": "Gesamt",
    "dueDate": "Fälligkeitsdatum",
    "all": "Alle",
    "recalculate": "Neu berechnen"
  },
  "es": {
    "ledgerEntries": "Partidas contables",
    "noLedgerEntries": "No se encontraron partidas",
    "amount": "Importe",
    "amountNet": "Importe (neto)",
    "amountToPay": "A pagar",
    "amountToInvoice": "A facturar",
    "taxRate": "Tipo impositivo",
    "description": "Descripción",
    "total": "Total",
    "dueDate": "Fecha de vencimiento",
    "all": "Todos",
    "recalculate": "Recalcular"
  }
}
</i18n>
